/* eslint-disable sonarjs/cognitive-complexity, eslint-comments/no-restricted-disable -- TODO: Refactor this file */
import { useMutation } from "react-query";
import { useHistory } from "react-router-dom";
import { paywallCheckoutApi } from "@circle-react/api/paywallCheckoutApi";
import { usePaywallCheckoutContext } from "@circle-react/contexts/Paywalls/paywallCheckoutContext";
import { stripeElementsModes } from "../../../StripeElements/types";
import { CheckoutError } from "../../CheckoutError";
import { constructReturnUrl, constructReturnUrlPath } from "../../helpers";
import { useGetStripeElements } from "../../useStripeElements";
import { verifyPayment } from "./helpers";
import { usePaymentMethodDataParams } from "./usePaymentMethodDataParams";

export const useCard = () => {
  const getStripeElements = useGetStripeElements();

  const history = useHistory();
  const {
    checkoutConfirmationUrl,
    currentCommunity,
    getSelectedPrice,
    isCardInfoRequired,
    isMemberUsingSavedPaymentMethod,
    stripeElementsMode,
    stripePaymentMethodType,
  } = usePaywallCheckoutContext();
  const { paymentMethodDataParams } = usePaymentMethodDataParams();

  // If we are using PMC, we only require a to setup a payment method if we are in setup mode.
  const isSettingUpPaymentMethod =
    stripeElementsMode === stripeElementsModes.setup;

  const verifyCheckoutAttemptPayment = async (
    checkoutResponseBody: any,
    tries: number,
  ) =>
    await verifyPayment(checkoutConfirmationUrl, checkoutResponseBody, tries);

  const submitStripeElement = async (caller: string): Promise<boolean> => {
    const { elements } = getStripeElements(caller);

    const result = await elements.submit();
    const error = result?.error;
    if (error) {
      throw CheckoutError.fromStripeError(caller, error);
    }
    return true;
  };

  const selectedPrice = getSelectedPrice();
  const processPaymentInStripe = async (checkoutResponseBody: any) => {
    const { stripe, elements } = getStripeElements(
      "useCard.processPaymentInStripe",
    );

    const {
      payment_intent_processor_id: paymentIntentProcessorId,
      payment_intent_client_secret: paymentIntentClientSecret,
    } = checkoutResponseBody;

    if (!paymentIntentProcessorId && !paymentIntentClientSecret) {
      // No payment intent? We are looking at a trial or $0 invoice. These cases don't require
      // further confirmation as they don't generate a payment intent.
      await verifyCheckoutAttemptPayment(checkoutResponseBody, 0);
      return;
    }

    const paymentIntentResult = await stripe.retrievePaymentIntent(
      paymentIntentClientSecret,
    );
    if (paymentIntentResult.error) {
      throw CheckoutError.fromStripeError(
        "useCard.processPaymentInStripe.retrievePaymentIntent",
        paymentIntentResult.error,
      );
    }

    const paymentIntent = paymentIntentResult?.paymentIntent;
    if (paymentIntent?.status === "succeeded") {
      await verifyCheckoutAttemptPayment(checkoutResponseBody, 0);
      return;
    }

    // If we reach this point and we required a setup intent, we need to resubmit the Stripe element
    // before we can confirm the payment intent. That's because the first submission was for the setup
    // intent, not the payment intent.
    if (isSettingUpPaymentMethod) {
      await submitStripeElement(
        "useCard.processPaymentInStripe.isSettingUpPaymentMethod",
      );
    }

    const confirmPaymentResult = await stripe.confirmPayment({
      elements,
      clientSecret: checkoutResponseBody.payment_intent_client_secret,
      confirmParams: {
        ...paymentMethodDataParams,
        return_url: constructReturnUrl(stripePaymentMethodType),
      },
      redirect: "if_required",
    });
    if (confirmPaymentResult.error) {
      throw CheckoutError.fromStripeError(
        "useCard.processPaymentInStripe.confirmPayment",
        confirmPaymentResult.error,
      );
    }

    // The async flow is enabled AND the payment confirmation was successful without a redirect
    // (e.g. modal 3DS or no 3DS needed). We must now manually trigger our verification handler
    // by pushing a new URL to history. This will trigger a re-render and the handler will then
    // take over and handle the rest of the flow.
    const verificationUrlPath = constructReturnUrlPath(
      stripePaymentMethodType,
      confirmPaymentResult.paymentIntent.id,
    );
    history.replace(verificationUrlPath);
  };

  const execCheckoutRequest = async (formData: any) => {
    const checkoutResponse = await paywallCheckoutApi.create_DEPRECATED({
      formData: {
        ...formData,
        payment_method_type: stripePaymentMethodType,
        community_member_adding_payment_method:
          !isMemberUsingSavedPaymentMethod,
      },
    });
    if (!checkoutResponse || !checkoutResponse.ok) {
      const error = await checkoutResponse?.json?.();
      throw CheckoutError.fromPaywallError(
        "useCard.execCheckoutRequest.create_DEPRECATED",
        error,
      );
    }

    const checkoutResponseBody = await checkoutResponse.json();
    await processPaymentInStripe(checkoutResponseBody);
  };

  const onSubmitMutation = useMutation<any, any, any>(async data => {
    const formData = data;
    const { elements, stripe } = getStripeElements("onSubmitMutation");

    if (isCardInfoRequired) {
      // Digital wallets (e.g. Apple and Google Pay) seem to have an acceptable time window between the
      // time the user clicks the pay button and the time the Payment Element is submitted. If we do not
      // do this as soon as possible, Apple/Google Pay will fail to load with an error message that says:
      // > Something went wrong. Unable to show Google Pay. Please choose a different payment method and try again.
      //
      // See PR for more context: https://github.com/circleco/circle/pull/22389
      await submitStripeElement("useCard.onSubmitMutation.isCardInfoRequired");

      if (isSettingUpPaymentMethod) {
        const prepareResponse = await paywallCheckoutApi.prepare_DEPRECATED(
          currentCommunity.id,
          selectedPrice.id,
        );
        if (!prepareResponse || !prepareResponse.ok) {
          const error = await prepareResponse?.json?.();
          throw CheckoutError.fromPaywallError(
            "useCard.onSubmitMutation.prepare_DEPRECATED",
            error,
          );
        }

        const prepareResponseBody = await prepareResponse.json();
        const setupIntentSecret =
          prepareResponseBody.setup_intent.client_secret;

        let setupIntentResult =
          await stripe.retrieveSetupIntent(setupIntentSecret);
        if (setupIntentResult.error) {
          throw CheckoutError.fromStripeError(
            "useCard.onSubmitMutation.retrieveSetupIntent",
            setupIntentResult.error,
          );
        }

        if (setupIntentResult.setupIntent.status !== "succeeded") {
          setupIntentResult = await stripe.confirmSetup({
            elements,
            clientSecret: setupIntentSecret,
            confirmParams: {
              return_url: window.location.href,
              ...paymentMethodDataParams,
            },
            redirect: "if_required",
          });
          if (setupIntentResult.error) {
            throw CheckoutError.fromStripeError(
              "useCard.onSubmitMutation.confirmSetup",
              setupIntentResult.error,
            );
          }

          formData.payment_method_processor_id =
            setupIntentResult.setupIntent.payment_method; // Pass payment method ID for checkout fulfillment.
        }
      }
    }

    await execCheckoutRequest(formData);
  });
  return { onSubmitMutation };
};
